import { Link } from "react-router-dom";
import { AtwLogo } from "@/components/AtwLogo";
import { ConnectionBrandIcon } from "@/components/ConnectionBrandIcon";
import { PublicLayout } from "@/components/PublicLayout";
import {
  SLACK_INSTALL_URL,
  SLACK_MARKETPLACE_APPROVED,
  SUPPORT_EMAIL,
} from "@/lib/publicSite";

const FEATURES = [
  {
    title: "Chat",
    body: "See your Slack conversations in Hub. Open one to read and reply, start a DM or group DM, and edit or delete your own messages. Messages load from Slack when you open them and aren't stored.",
  },
  {
    title: "Needs you, on Hub Home",
    body: "Recent mentions and DMs that are waiting on your reply. Hub asks Slack's search when you open Home and doesn't store the results.",
  },
  {
    title: "Send to Hub",
    body: "In Slack, open a message's More actions menu and choose Send to Hub to save a link to it on Hub Home. Hub saves the link, not the message text.",
  },
  {
    title: "Hub AI",
    body: "Ask about your Slack, like “What needs my reply today?” When you ask, and only then, Hub AI searches and reads Slack through Slack's MCP server. It can draft a reply, and nothing is sent until you press Send.",
    note: "Hub AI uses an AI model and can get things wrong. Check its answers and drafts before you rely on them.",
  },
  {
    title: "Home tab in Slack",
    body: "The ATW Hub app's Home tab in Slack shows how to get started, with links to support and privacy.",
  },
];

const PERMISSIONS = [
  {
    what: "Your conversation list, and messages in the channels, private channels, DMs, and group DMs you're in",
    why: "To show Chat",
  },
  {
    what: "Send messages as you, and start DMs and group DMs",
    why: "To reply from Hub. Hub sends only when you press Send.",
  },
  {
    what: "Search the messages you can see",
    why: "For Needs you and Hub AI",
  },
  {
    what: "People's names and profile photos",
    why: "To show who wrote each message",
  },
  {
    what: "A message shortcut and a bot user",
    why: "For Send to Hub and the Home tab in Slack",
  },
];

function AddToSlackButton() {
  return (
    <a href={SLACK_INSTALL_URL} className="public-slack-button" data-hub-same-tab="1">
      <img
        alt="Add to Slack"
        height={40}
        width={139}
        src="https://platform.slack-edge.com/img/add_to_slack.png"
        srcSet="https://platform.slack-edge.com/img/add_to_slack.png 1x, https://platform.slack-edge.com/img/add_to_slack@2x.png 2x"
      />
    </a>
  );
}

export function SlackLandingPage() {
  return (
    <PublicLayout title="ATW Hub for Slack">
      <section className="public-hero">
        <p className="public-eyebrow">Slack</p>
        <div className="public-hero-marks" aria-hidden>
          <AtwLogo size={44} label="" />
          <span className="public-hero-plus">+</span>
          <ConnectionBrandIcon id="slack" size={44} />
        </div>
        <h1>ATW Hub for Slack</h1>
        <p className="public-lede">
          Read and answer your Slack conversations in ATW Hub, next to your email, calendar, and
          meetings.
        </p>
        <div className="public-hero-actions">
          <AddToSlackButton />
          <Link to="/privacy">Privacy policy</Link>
          <Link to="/support">Support</Link>
        </div>
      </section>

      <section className="public-section">
        <h2>What ATW Hub is</h2>
        <p>
          ATW Hub (<a href="https://hub.atw.net">hub.atw.net</a>) is one place for a business's
          communication: mail, chat, calendar, meetings, and an AI assistant. The ATW Hub app for Slack
          brings your Slack conversations into Hub, so you can see what needs you and reply without
          switching apps.
        </p>
      </section>

      <section className="public-section">
        <h2>What it does</h2>
        <div className="public-feature-grid">
          {FEATURES.map((feature) => (
            <article key={feature.title} className="public-card">
              <h3>{feature.title}</h3>
              <p>{feature.body}</p>
              {feature.note ? <p className="public-note">{feature.note}</p> : null}
            </article>
          ))}
        </div>
        {SLACK_MARKETPLACE_APPROVED ? null : (
          <p className="public-callout">
            <strong>Before Slack Marketplace approval:</strong> Slack limits apps that aren't in the
            Marketplace yet. Hub can load message history only once a minute, 15 messages at a time, so
            opening several conversations in a row is slow. Needs you and Hub AI turn on after approval.
          </p>
        )}
      </section>

      <section className="public-section public-doc-section" id="install">
        <h2>Add it to your Slack</h2>
        <p>
          You need an ATW account with Hub turned on. Your organization's ATW admin can turn it on. If
          your business doesn't use ATW Hub yet, email{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> to get started.
        </p>
        <ol className="public-steps">
          <li>Click Add to Slack, pick your workspace, and review what Hub asks for.</li>
          <li>Click Allow. Slack sends you back to Hub to finish setup.</li>
          <li>Open Chat in Hub to see your conversations, and Home to see what needs you.</li>
          <li>In Slack, open the ATW Hub app's Home tab for tips and help links.</li>
        </ol>
        <div className="public-hero-actions">
          <AddToSlackButton />
        </div>
        <p className="muted">
          If your Slack workspace requires approval for new apps, Slack asks a workspace admin first.
        </p>
      </section>

      <section className="public-section">
        <h2>Your data, in plain words</h2>
        <ul className="public-list">
          <li>
            Hub reads Slack only when you do something: open a conversation, open Home, use Send to Hub,
            or ask Hub AI. It doesn't scan your workspace in the background.
          </li>
          <li>Hub uses your own Slack sign-in, so it sees only what you can already see in Slack.</li>
          <li>
            Hub doesn't store message text, files, or search results. It stores your Slack sign-in
            tokens (encrypted), which conversations you've read, and the links you save with Send to Hub.
          </li>
          <li>Slack data is never used to train AI models and is never shared with other organizations.</li>
          <li>
            Disconnect Slack in Hub, or uninstall the app from Slack, and Hub deletes your tokens, read
            markers, and saved links.
          </li>
        </ul>
        <p>
          <Link to="/privacy">Read the full privacy policy</Link>
        </p>
      </section>

      <section className="public-section">
        <h2>What Hub asks Slack for</h2>
        <div className="public-table-wrap">
          <table className="public-table">
            <thead>
              <tr>
                <th scope="col">Access</th>
                <th scope="col">Why</th>
              </tr>
            </thead>
            <tbody>
              {PERMISSIONS.map((row) => (
                <tr key={row.what}>
                  <td data-label="Access">{row.what}</td>
                  <td data-label="Why">{row.why}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="public-section">
        <h2>Help</h2>
        <p>
          Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. You don't need an account to
          reach us, and we reply within 2 business days. See <Link to="/support">Support</Link> for
          answers to common questions.
        </p>
      </section>
    </PublicLayout>
  );
}
